const target = (name) => `[data-tour="${name}"]`;

export const TOURS = {
  app: {
    label: "App tour",
    seenKey: "tour_app",
    route: "/rfp-dashboard",
    expandNav: true,
    steps: [
      {
        target: target("nav-sensa"),
        title: "Meet Sensa, your AI bid assistant",
        body: "Upload an RFP and Sensa will summarise it, score it and draft responses from your knowledge base.",
      },
      {
        target: target("nav-knowledge-base"),
        title: "Build your Knowledge Base",
        body: "Upload past proposals, security docs and case studies, or connect Google Drive and Salesforce. The more Sensa knows, the stronger its answers.",
      },
      {
        target: target("nav-projects"),
        title: "Manage your bids in Projects",
        body: "One project per bid: files, requirements, team and a guided Setup, Draft, Review and Export flow.",
      },
      {
        target: target("nav-tender-search"),
        title: "Discover new opportunities",
        body: "Browse federal tenders, filter to your strengths, and save searches to get notified of new matches.",
      },
      {
        target: target("nav-extractor"),
        title: "Extract data from any document",
        body: "Build a template once in Template Builder and pull the same fields out of every invoice, contract or form. Library has ready-made templates to start from.",
      },
      {
        target: target("workspace"),
        title: "Collaborate with your team",
        body: "Switch workspaces and invite teammates from Organization Settings. Great proposals are a team effort.",
        primary: "Finish tour",
      },
    ],
  },
  projects: {
    label: "Projects",
    seenKey: "tour_projects",
    route: "/rfp-projects/my-projects",
    steps: [
      {
        target: target("create-project"),
        title: "Start with a project",
        body: "Click Create Project. One project per RFP keeps files, requirements and your team organised in one place.",
        advanceOnTargetClick: true,
      },
      {
        target: target("project-name"),
        title: "Name your project",
        body: "Client name plus scope works well. Add the RFP files below, and Sensa extracts every requirement once the project is created.",
        primary: "Got it",
      },
    ],
  },
  tender: {
    label: "Tender Search",
    seenKey: "tour_tender",
    route: "/tender-search",
    steps: [
      {
        target: target("tender-search"),
        title: "Search federal tenders",
        body: "Search by title, agency, solicitation number or description to find opportunities that match your offering.",
      },
      {
        target: target("tender-stats"),
        title: "Filter with one click",
        body: "Each figure is also a filter. Select Closing in 7 days, for example, to see the tenders that need attention now.",
      },
      {
        target: target("tender-rail"),
        title: "Refine your results",
        body: "Narrow by status, notice type, industry, location, agency, set-aside, dates and documents. Counts update as you go.",
      },
      {
        target: '[data-tour="tender-results"] > *:first-child',
        title: "Review a tender",
        body: "Each row shows the notice type, agency and closing date. Open a tender to view its documents and create a project from it.",
      },
      {
        target: target("tender-save"),
        title: "Save your search",
        body: "Save these filters to return to them later and get daily or weekly email alerts when new tenders match.",
        primary: "Finish",
      },
    ],
  },
};

const steps = (rows) => rows.map(([t, d], i) => ({ n: i + 1, t, d }));
const rows = (pairs) => pairs.map(([a, b]) => ({ a, b }));

export const INTROS = {
  intro_projects: {
    name: "Projects",
    route: "/rfp-projects/my-projects",
    matches: (path) => path.startsWith("/rfp-projects/my-projects"),
    tag: "Manage every bid in one place. Upload the RFP and Sensa extracts the requirements so your team can focus on the response.",
    steps: steps([
      ["Create a project", "One per bid, named after the client and scope."],
      ["Drop in the RFP", "Upload files or add portal links. PDF, Word and spreadsheet formats are supported."],
      ["Let Sensa read it", "Requirements, deadlines and pass/fail criteria are extracted automatically."],
      ["Build the proposal", "Setup, Draft, Review and Export, with your team in the loop."],
    ]),
    snipTitle: "City of Austin, IT Services",
    chip: "Due Oct 28",
    rows: rows([["Requirements found", "132"], ["Pass/fail requirements", "9"], ["Stage", "Setup"]]),
    cta: "Start tour",
    tour: "projects",
  },
  intro_tender: {
    name: "Tender Search",
    route: "/tender-search",
    matches: (path) => path === "/tender-search",
    tag: "Search federal tenders in one place, shortlist the right opportunities and turn them into projects.",
    steps: steps([
      ["Search", "Find tenders by title, agency, solicitation number or description."],
      ["Filter", "Narrow by industry, location, set-aside, notice type and closing date."],
      ["Review", "Open a tender to see its documents, deadline and fit."],
      ["Save and track", "Save searches and get email alerts for new matches, or create a project from any tender."],
    ]),
    snipTitle: "Results",
    chip: "Saved · 4 new",
    rows: rows([["IT Modernization Support", "3 days left"], ["Cloud Migration Services", "Due Nov 20"], ["Data Analytics Platform", "Due Dec 02"]]),
    cta: "Start tour",
    tour: "tender",
  },
  intro_kb: {
    name: "Knowledge Base",
    route: "/rfp-knowledgeBase/All-sources",
    matches: (path) => path.toLowerCase().startsWith("/rfp-knowledgebase/") && !path.toLowerCase().endsWith("/chat"),
    tag: "The content Sensa draws on. Everything you add here improves its answers and is cited as a source.",
    steps: steps([
      ["Add sources", "From your device, Google Drive or Salesforce."],
      ["Stay organised", "Use folders and tags to keep content organised."],
      ["Get cited answers", "Sensa links each answer to its source."],
    ]),
    snipTitle: "All sources",
    chip: "Synced from Drive",
    rows: rows([["Security_Whitepaper_2026.pdf", "PDF"], ["Proposal, State of Ohio.docx", "DOCX"], ["Case study, Metro Transit.pdf", "PDF"]]),
    cta: "Add my first docs",
  },
  intro_dashboard: {
    name: "Dashboard",
    route: "/rfp-dashboard",
    matches: (path) => path === "/rfp-dashboard",
    tag: "Your full bid pipeline on one screen, so you can see what needs attention first.",
    steps: steps([
      ["See what's due", "Bid end dates across every project."],
      ["Track progress", "View the status of each project."],
      ["Take action", "Open any project directly from the dashboard."],
    ]),
    snipTitle: "This week",
    chip: "Live",
    rows: rows([["Due this week", "3"], ["In review", "5"], ["Submitted", "12"]]),
    cta: "View dashboard",
  },
  intro_analytics: {
    name: "Analytics",
    route: "/analytics",
    matches: (path) => path.startsWith("/analytics"),
    tag: "See how well Sensa is answering and where your knowledge base has gaps.",
    steps: steps([
      ["Response evaluation", "How Sensa's answers score."],
      ["Knowledge gaps", "Questions with thin or no sources."],
      ["Close the gaps", "Add the missing docs to your Knowledge Base."],
    ]),
    snipTitle: "Last 30 days",
    chip: "2 tabs",
    rows: rows([["Answer quality", "86%"], ["Knowledge gaps", "7"], ["Top missing", "SOC 2 report"]]),
    cta: "View analytics",
  },
  intro_template: {
    name: "Template Builder",
    route: "/extractor",
    matches: (path, search) => path === "/extractor" && !search.includes("view=library"),
    tag: "Define the fields you need once, and DocuSensa extracts them from every file.",
    steps: steps([
      ["Pick a document type", "Invoices, contracts, forms and more."],
      ["Define fields", "Set a name, type and description for each field."],
      ["Run and export", "Review the results, then download a CSV."],
    ]),
    snipTitle: "Invoice template",
    chip: "3 fields",
    rows: rows([["invoice_number", "Text"], ["total_amount", "Currency"], ["due_date", "Date"]]),
    cta: "Build a template",
  },
  intro_library: {
    name: "Library",
    route: "/extractor?view=library",
    matches: (path, search) => path === "/extractor" && search.includes("view=library"),
    tag: "Start from a ready-made template and adapt it to your needs.",
    steps: steps([
      ["Browse", "Ready-made templates for common documents."],
      ["Import", "Add a template to your workspace."],
      ["Customize", "Add, remove or rename fields."],
    ]),
    snipTitle: "Starter templates",
    chip: "Import",
    rows: rows([["Invoice (standard)", "12 fields"], ["W-9 form", "8 fields"], ["Master services agreement", "15 fields"]]),
    cta: "Browse templates",
  },
};

export const CHECKLIST_ITEMS = {
  tour: {
    label: "Take the product tour",
    hint: "A one-minute look at the main areas of DocuSensa.",
    tour: "app",
  },
  project: {
    label: "Create your first project from an RFP",
    hint: "Upload an RFP and Sensa extracts every requirement.",
    tour: "projects",
  },
  kb: {
    label: "Add documents to your Knowledge Base",
    hint: "Past proposals and policies make Sensa's answers stronger.",
    route: "/rfp-knowledgeBase/All-sources",
  },
  sensa: {
    label: "Ask Sensa a question",
    hint: "Try asking whether an RFP is worth bidding on.",
    route: "/rfp-copilot",
  },
  tender: {
    label: "Save a tender search",
    hint: "Get alerted when matching tenders are posted.",
    tour: "tender",
  },
  template: {
    label: "Build an extraction template",
    hint: "Pull the same fields out of every invoice, contract or form.",
    route: "/extractor",
  },
  invite: {
    label: "Invite a teammate",
    hint: "Proposals go faster with your team in one workspace.",
    route: "/account/team",
  },
};

// Order follows what the person told us at signup.
export const checklistFor = ({ role, useCase }) => {
  if (useCase === "extract") return ["tour", "template", "kb", "sensa", "invite"];
  if ((role || "").startsWith("Sales")) return ["tour", "tender", "project", "sensa", "invite"];
  return ["tour", "project", "kb", "sensa", "invite"];
};
